/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type ReactElement, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Await, useLoaderData, redirect } from 'react-router';
import { routes, routeHref } from '@/route-paths';
import type { Route } from './+types/_app.account.orders._index';
import { useNavigate } from '@/hooks/use-navigate';
import { OrderListHeader, OrderListSkeleton } from '@/components/account/order-list';
import { OrderHistoryView, type OrderHistoryInitial } from '@/components/account/order-list/order-history-view';
import { fetchCustomerOrdersPage, fetchFailedOrderCount, DEFAULT_ORDERS_LIMIT } from '@/lib/api/order.server';
import { Card, CardContent } from '@/components/ui/card';
import { Typography } from '@/components/typography';
import { SeoMeta } from '@/components/seo-meta';
import { buildUrlFromContext } from '@/lib/url.server';
import { getLogger } from '@/lib/logger.server';
import { getAuth } from '@/middlewares/auth.server';
import { UITarget } from '@/targets/ui-target';

type OrderListLoaderData = {
    ordersPromise: Promise<OrderHistoryInitial>;
};

/**
 * Loader fetches the first window of the customer's order history via SCAPI getCustomerOrders.
 * Returns a deferred promise for streaming/suspense support.
 *
 * RGBARRY-CUSTOMIZATION (order history: hide failed orders + "View more"): the list no longer reads
 * offset/limit from the URL — it uses a forward cursor streamed client-side (see OrderHistoryView).
 * The loader fetches only the first window and, in parallel, the failed-order count so the footer can
 * show a "Viewing X of Y orders" count with failed orders excluded (Y = physical total − failed).
 */
export function loader({ context }: Route.LoaderArgs): OrderListLoaderData {
    const logger = getLogger(context);
    logger.debug('OrderList: loader starting');

    // Get customer ID from auth session
    const session = getAuth(context);
    if (!session.customerId) {
        logger.warn('OrderList: no customerId, redirecting to login');
        throw redirect(buildUrlFromContext('/login', context));
    }

    const customerId = session.customerId;

    // Compose the first window + the visible total in the loader (stable promise identity for Suspense).
    // fetchFailedOrderCount degrades to null on failure, so the count is best-effort and never blocks.
    const ordersPromise = Promise.all([
        fetchCustomerOrdersPage(context, customerId, { offset: 0, limit: DEFAULT_ORDERS_LIMIT }),
        fetchFailedOrderCount(context, customerId),
    ]).then(([page, failedCount]) => ({
        ...page,
        visibleTotal: failedCount == null ? null : Math.max(0, page.allTotal - failedCount),
    }));

    return {
        ordersPromise,
    };
}

/**
 * Error state for order list.
 */
function OrderListError(): ReactElement {
    const { t } = useTranslation('account');
    return (
        <Card className="border-muted-foreground/10">
            <CardContent className="p-12 text-center space-y-4">
                <Typography variant="p" className="text-muted-foreground">
                    {t('orders.errorDescription')}
                </Typography>
            </CardContent>
        </Card>
    );
}

/**
 * Order list page – renders at /account/orders.
 * Order details at /account/orders/:orderNo.
 */
export default function OrderListPage(): ReactElement {
    const { t } = useTranslation('account');
    const navigate = useNavigate();
    const loaderData = useLoaderData<typeof loader>();

    const handleViewDetails = (orderNo: string) => {
        // oxlint-disable-next-line @typescript-eslint/no-floating-promises -- navigate() result intentionally not awaited
        navigate(routeHref(routes.accountOrderDetail, { orderNo }));
    };

    return (
        <div className="order-history-page text-sm space-y-0">
            <SeoMeta title={t('meta.orderHistoryTitle', { defaultValue: 'Order History' })} noIndex />
            <OrderListHeader title={t('navigation.orderHistory')} subtitle={t('orders.subtitle')} />
            <Suspense fallback={<OrderListSkeleton />}>
                {/* RGBARRY-CUSTOMIZATION (order history): the resolved window feeds OrderHistoryView,
                    which streams further windows client-side behind a "View more" button. */}
                <Await resolve={loaderData.ordersPromise} errorElement={<OrderListError />}>
                    {(initial) => <OrderHistoryView initial={initial} onViewDetails={handleViewDetails} />}
                </Await>
            </Suspense>
            <UITarget targetId="sfcc.myAccount.orders.tracking" />
        </div>
    );
}
